const HUBSPOT_SCRIPT_ID = 'ezyquip-hubspot-forms-script';
const HUBSPOT_SCRIPT_URL = 'https://js.hsforms.net/forms/embed/v2.js';
const FORM_TIMEOUT_MS = 20000;

let hubSpotScriptPromise;

function loadHubSpotScript() {
  if (window.hbspt?.forms?.create) {
    return Promise.resolve(window.hbspt);
  }

  if (hubSpotScriptPromise) return hubSpotScriptPromise;

  hubSpotScriptPromise = new Promise((resolve, reject) => {
    let script = document.getElementById(HUBSPOT_SCRIPT_ID) ||
        document.querySelector(`script[src*="js.hsforms.net/forms/embed/v2.js"]`);

    const handleLoad = () => {
      if (window.hbspt?.forms?.create) {
        resolve(window.hbspt);
      } else {
        reject(new Error('HubSpot Forms API is unavailable'));
      }
    };

    if (script) {
      if (window.hbspt?.forms?.create) {
        resolve(window.hbspt);
        return;
      }

      script.addEventListener('load', handleLoad, {once: true});
      script.addEventListener(
          'error',
          () => reject(new Error('Unable to load HubSpot Forms')),
          {once: true}
      );
      return;
    }

    script = document.createElement('script');
    script.id = HUBSPOT_SCRIPT_ID;
    script.src = HUBSPOT_SCRIPT_URL;
    script.async = true;
    script.defer = true;
    script.addEventListener('load', handleLoad, {once: true});
    script.addEventListener(
        'error',
        () => reject(new Error('Unable to load HubSpot Forms')),
        {once: true}
    );
    document.head.append(script);
  }).catch((error) => {
    hubSpotScriptPromise = null;
    throw error;
  });

  return hubSpotScriptPromise;
}

function getFormElement(hubSpotForm) {
  return hubSpotForm?.[0] || hubSpotForm || null;
}

function setHiddenField(form, name, value, className = '') {
  if (!form || !name) return;

  let input = [...form.querySelectorAll('input')].find(
      (candidate) => candidate.name === name
  );

  if (!input) {
    input = document.createElement('input');
    input.type = 'hidden';
    input.name = name;
    form.append(input);
  }

  input.value = value ?? '';
  if (className) input.classList.add(className);
}

function applyConfiguredHiddenFields(shell, form) {
  if (!shell.dataset.hiddenFields) return;

  try {
    const hiddenFields = JSON.parse(shell.dataset.hiddenFields);
    Object.entries(hiddenFields).forEach(([name, value]) => {
      setHiddenField(form, name, value, 'ezyquip-custom-hidden-input');
    });
  } catch (error) {
    console.error('Unable to read HubSpot hidden fields:', error);
  }
}

function readQuoteCart() {
  try {
    const cart = JSON.parse(
        window.localStorage.getItem('quoteBuilderCart') || '[]'
    );
    return Array.isArray(cart) ? cart : [];
  } catch (error) {
    console.error('Unable to read quote cart:', error);
    return [];
  }
}

function syncQuoteHiddenFields(form) {
  form.querySelectorAll('.quote-custom-hidden-input').forEach((input) => {
    input.remove();
  });

  readQuoteCart().forEach((item, index) => {
    const fieldNumber = index + 1;
    setHiddenField(
        form,
        `quote_item_${fieldNumber}_name`,
        item.name,
        'quote-custom-hidden-input'
    );
    setHiddenField(
        form,
        `quote_item_${fieldNumber}_product_id`,
        item.specs || item.productId || item.id || '',
        'quote-custom-hidden-input'
    );
    setHiddenField(
        form,
        `quote_item_${fieldNumber}_quantity`,
        item.quantity || 1,
        'quote-custom-hidden-input'
    );
  });
}

function prepareNewsletterForm(form) {
  const emailField = form.querySelector('.hs_email');
  const firstNameField = form.querySelector('.hs_firstname');

  const adjustField = (field, labelText) => {
    if (!field) return null;

    const label = field.querySelector('label');
    const input = field.querySelector('.hs-input');
    const inputWrapper = field.querySelector('.input');

    if (!label || !input || !inputWrapper) return null;

    const labelSpan = label.querySelector('span:not(.hs-form-required)');
    if (labelSpan) labelSpan.textContent = labelText;

    const requiredMarker = label.querySelector('.hs-form-required');
    if (requiredMarker) requiredMarker.style.display = 'none';

    field.insertBefore(label, inputWrapper);
    return inputWrapper;
  };

  const emailInputWrapper = adjustField(emailField, 'Email Address');
  const firstNameInputWrapper = adjustField(firstNameField, 'First Name');

  if (
    emailField &&
    firstNameField &&
    emailField.nextElementSibling !== firstNameField
  ) {
    firstNameField.parentNode.insertBefore(emailField, firstNameField);
  }

  if (emailInputWrapper) {
    let arrow = form.querySelector('.hs-custom-arrow');
    if (!arrow) {
      arrow = document.createElement('div');
      arrow.className = 'hs-custom-arrow';
      arrow.innerHTML = '<svg width="24" height="24" viewBox="0 0 24 24" fill="none" aria-hidden="true"><path d="M9 18L15 12L9 6" stroke="#DCDCDC" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"/></svg>';
    }
    emailInputWrapper.append(arrow);
  }

  if (firstNameInputWrapper) {
    const submitButton = form.querySelector('.hs_submit');
    if (submitButton) firstNameInputWrapper.append(submitButton);
  }
}

function markFormReady(shell, form) {
  window.clearTimeout(shell.hubSpotTimeout);
  shell.classList.remove('is-loading', 'is-error');
  shell.classList.add('is-ready');

  const target = shell.querySelector('.hubspot-form-target');
  if (target) target.setAttribute('aria-busy', 'false');

  shell.dispatchEvent(new CustomEvent('ezyquip:hubspot-form-ready', {
    bubbles: true,
    detail: {
      context: shell.dataset.formContext || 'default',
      form,
    },
  }));
}

function markFormError(shell) {
  shell.classList.remove('is-loading');
  shell.classList.add('is-error');

  const loaderText = shell.querySelector('.hubspot-form-loader__text');
  if (loaderText) {
    loaderText.textContent = 'The form is taking longer than expected. Please refresh and try again.';
  }
}

function handleFormReady(shell, hubSpotForm) {
  const form = getFormElement(hubSpotForm);
  if (!form) {
    markFormError(shell);
    return;
  }

  const context = shell.dataset.formContext || 'default';
  applyConfiguredHiddenFields(shell, form);

  if (context === 'quote' || context === 'global-quote') {
    syncQuoteHiddenFields(form);
    shell.quoteCartHandler = () => syncQuoteHiddenFields(form);
    document.addEventListener('quoteCartUpdated', shell.quoteCartHandler);
  }

  if (context === 'newsletter') {
    prepareNewsletterForm(form);
  }

  markFormReady(shell, form);
}

function handleBeforeSubmit(shell, hubSpotForm) {
  const form = getFormElement(hubSpotForm);
  if (!form) return;

  const context = shell.dataset.formContext || 'default';
  applyConfiguredHiddenFields(shell, form);

  if (context === 'quote' || context === 'global-quote') {
    syncQuoteHiddenFields(form);
  }

  shell.dispatchEvent(new CustomEvent('ezyquip:hubspot-form-submit', {
    bubbles: true,
    detail: {
      context,
      hiddenFieldNames: [...form.querySelectorAll('input[type="hidden"]')]
          .map((input) => input.name)
          .filter(Boolean),
    },
  }));
}

function handleFormSubmitted(shell) {
  const context = shell.dataset.formContext || 'default';

  if (context === 'contact') {
    const block = shell.closest('.contact_us_block');
    const requiredText = block?.querySelector('.form-required');
    if (requiredText) requiredText.style.display = 'none';

    if (block) {
      const offset = block.getBoundingClientRect().top + window.scrollY - 100;
      window.scrollTo({top: offset, behavior: 'smooth'});
    }
  }

  if (context === 'quote') {
    window.setTimeout(() => {
      shell.closest('.quote_block')?.classList.add('quote-success-state');
    }, 50);
  }

  if (context === 'global-quote') {
    try {
      window.localStorage.setItem('quoteBuilderCart', JSON.stringify([]));
    } catch (error) {
      console.error('Unable to clear quote cart:', error);
    }

    window.setTimeout(() => {
      document.getElementById('global-quote-modal')
          ?.classList.add('global-success-state');
      const infoBlock = document.getElementById('global-quote-success-info');
      if (infoBlock) infoBlock.style.display = 'flex';
    }, 50);
  }
}

async function mountHubSpotForm(shell, force = false) {
  if (!shell || (!force && shell.dataset.hubspotState)) return;

  const target = shell.querySelector('.hubspot-form-target');
  const portalId = shell.dataset.portalId;
  const formId = shell.dataset.formId;
  const region = shell.dataset.region || 'na1';

  if (!target || !portalId || !formId) {
    markFormError(shell);
    return;
  }

  if (shell.quoteCartHandler) {
    document.removeEventListener('quoteCartUpdated', shell.quoteCartHandler);
    delete shell.quoteCartHandler;
  }

  target.replaceChildren();
  target.setAttribute('aria-busy', 'true');
  const loaderText = shell.querySelector('.hubspot-form-loader__text');
  if (loaderText) loaderText.textContent = 'Loading form…';
  shell.classList.remove('is-ready', 'is-error');
  shell.classList.add('is-loading');
  shell.dataset.hubspotState = 'loading';
  shell.hubSpotTimeout = window.setTimeout(() => markFormError(shell), FORM_TIMEOUT_MS);

  try {
    const hbspt = await loadHubSpotScript();
    hbspt.forms.create({
      portalId,
      formId,
      region,
      target: `#${target.id}`,
      onFormReady: (form) => {
        shell.dataset.hubspotState = 'ready';
        handleFormReady(shell, form);
      },
      onFormSubmit: (form) => handleBeforeSubmit(shell, form),
      onFormSubmitted: () => handleFormSubmitted(shell),
    });
  } catch (error) {
    console.error(error);
    shell.dataset.hubspotState = 'error';
    markFormError(shell);
  }
}

export function hubSpotFormsInit() {
  const shells = [...document.querySelectorAll('[data-hubspot-form]')];
  if (!shells.length) return;

  // Mount immediately after DOMContentLoaded; no viewport/scroll gating.
  shells.forEach((shell) => mountHubSpotForm(shell));

  window.ezyquipHubSpotForms = {
    reload(target) {
      const shell = typeof target === 'string'
          ? document.querySelector(target)
          : target;
      return mountHubSpotForm(shell, true);
    },
  };
}
